/**
 * PreviewDataSpecialist — pixel-faithful render of the ClaimSync "Data Specialist
 * Dashboard" template (Daylight variant supplied), with a light/dark toggle.
 *
 * The greeting counts + metric rail ("claims in data stage"), the Active Wizards
 * list, and the queue header's subject claim are wired to REAL claims (from
 * /api/v1/reports). The per-transaction review rows, the classifier confidence
 * tiers, and the ingestion file list use the template's representative content —
 * there is no per-transaction-classification endpoint a design-review surface can
 * cheaply call, so those are clearly static preview data.
 *
 * Mounted at /preview/data-specialist. Self-contained — touches no working
 * surface, no flags. Black + orange discipline; green ("done") only on
 * completed/parsed states + the auto-classify-rate metric.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import {
  SERIF, BODY, MONO, PREVIEW_ZOOM, Palette, ChipKind,
  useTheme, ThemeToggle, chipStyle, fmtUSD, relTime,
} from './preview/previewTheme';

interface ApiReport {
  id: string;
  client_name?: string;
  status?: string;
  event_type?: string;
  created_at?: string;
  updated_at?: string;
  result?: { final_claim_amount?: number };
}

// ---- which claims are still in the "data" stage (pre-analysis) ----
function inDataStage(status?: string): boolean {
  const s = (status || '').toLowerCase();
  return s === 'draft' || s === 'in_progress' || s === 'processing' || s === 'review' || s === 'in_review' || s === '';
}

// ---- nav model (representative; counts where they map to real data) ----
interface NavItem { id: string; label: string; icon: string; badge?: string; active?: boolean; }
interface NavGroup { group: string; items: NavItem[]; }

function NavRow({ P, item, onClick }: { P: Palette; item: NavItem; onClick?: () => void }) {
  const [hover, setHover] = useState(false);
  const active = !!item.active;
  return (
    <a
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 11, padding: '8px 18px 8px 19px',
        fontSize: 13.5, fontWeight: 500, cursor: 'pointer', textDecoration: 'none',
        borderLeft: `3px solid ${active ? P.orange : 'transparent'}`,
        color: active || hover ? P.textPrimary : P.textLabel,
        background: active || hover ? P.elevBg : 'transparent',
      }}
    >
      <span style={{ fontSize: 14, width: 18, textAlign: 'center', opacity: 0.9 }}>{item.icon}</span>
      <span style={{ flex: 1 }}>{item.label}</span>
      {item.badge && (
        <span style={{ fontSize: 10.5, fontWeight: 600, background: P.orange, color: P.onOrange, borderRadius: 999, padding: '1px 7px', minWidth: 18, textAlign: 'center' }}>{item.badge}</span>
      )}
    </a>
  );
}

// ---- representative content (template) ----
interface Legend { label: string; dotColor: string; }

type Tier = 'rule' | 'ml' | 'ai';
interface Txn { date: string; desc: string; amount: string; cat: string; review: boolean; tier: Tier; tierLabel: string; pct: string; }

const TXNS: Txn[] = [
  { date: '03/14', desc: 'SYSCO FOODS #8841', amount: '$4,820.00', cat: 'COGS — Food', review: false, tier: 'rule', tierLabel: 'Rule', pct: '97%' },
  { date: '03/15', desc: 'ADP PAYROLL', amount: '$18,240.00', cat: 'Payroll', review: false, tier: 'ml', tierLabel: 'ML', pct: '92%' },
  { date: '03/18', desc: 'CINTAS UNIFORM SVC', amount: '$612.40', cat: 'Operating exp.', review: false, tier: 'ml', tierLabel: 'ML', pct: '88%' },
  { date: '03/21', desc: 'TXU ENERGY', amount: '$2,118.00', cat: 'Utilities', review: false, tier: 'rule', tierLabel: 'Rule', pct: '96%' },
  { date: '03/24', desc: 'ZELLE TRANSFER · J. REYES', amount: '$9,400.00', cat: 'Uncategorized — owner?', review: true, tier: 'ai', tierLabel: 'Review', pct: '61%' },
  { date: '03/26', desc: 'AMEX EPAYMENT', amount: '$3,300.00', cat: 'Owner draw vs. COGS', review: true, tier: 'ai', tierLabel: 'Review', pct: '58%' },
  { date: '03/28', desc: 'SQUARE INC · DEPOSIT', amount: '$14,720.00', cat: 'Revenue', review: false, tier: 'rule', tierLabel: 'Rule', pct: '94%' },
];

type IngestKind = 'done' | 'parsing' | 'queued';
interface Ingest { icon: string; name: string; meta: string; status: string; kind: IngestKind; }

const INGEST: Ingest[] = [
  { icon: '▦', name: 'Bank statement · Mar 2023.pdf', meta: 'OCR tier 3 · 2 of 4 pages', status: 'Parsing', kind: 'parsing' },
  { icon: '❏', name: 'QuickBooks_2023.IIF', meta: '1,284 transactions imported', status: 'Parsed', kind: 'done' },
  { icon: '§', name: 'Form 1065 + K-1.pdf', meta: 'Schedule data extracted', status: 'Parsed', kind: 'done' },
  { icon: '▤', name: 'CreditCard_Q1.csv', meta: 'awaiting classifier', status: 'Queued', kind: 'queued' },
];

export default function PreviewDataSpecialist() {
  const { theme, P, toggle } = useTheme();
  const navigate = useNavigate();
  const [reports, setReports] = useState<ApiReport[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/api/v1/reports?limit=200', { showErrorToast: false });
        const list: ApiReport[] = Array.isArray(data?.reports) ? data.reports : Array.isArray(data) ? data : [];
        if (alive) setReports(list);
      } catch {
        if (alive) setReports([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  // most-recently updated first
  const sorted = useMemo(() => [...reports].sort((a, b) =>
    new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime()), [reports]);

  const dataStage = useMemo(() => sorted.filter((r) => inDataStage(r.status)), [sorted]);
  // wizards = mid-intake claims (draft); fall back to template if none
  const wizardClaims = useMemo(() => sorted.filter((r) => (r.status || '').toLowerCase() === 'draft').slice(0, 2), [sorted]);

  // wizard rows: real draft claims where available, else template representatives.
  // step/pct/stage are representative (no per-claim wizard-progress endpoint).
  interface WizardRow { key: string; id?: string; name: string; step: number; pct: string; stage: string; }
  const wizardRows: WizardRow[] = useMemo(() => {
    const tmpl = [
      { step: 3, pct: '43%', stage: 'Step 3 · Financials & P&L upload' },
      { step: 1, pct: '8%', stage: 'Step 1 · Policy & loss details' },
    ];
    if (wizardClaims.length) {
      return wizardClaims.map((w, i) => ({
        key: w.id, id: w.id, name: w.client_name || 'Untitled claim',
        step: tmpl[i]?.step ?? 1, pct: tmpl[i]?.pct ?? '8%', stage: tmpl[i]?.stage ?? 'Step 1 · Policy & loss details',
      }));
    }
    return [
      { key: 't0', name: 'Prairie Press Printing', ...tmpl[0] },
      { key: 't1', name: 'Untitled claim', ...tmpl[1] },
    ];
  }, [wizardClaims]);

  const dataStageCount = dataStage.length;
  const queueCount = 23; // representative review-queue depth (no per-txn endpoint)
  const subject = sorted[0] || null;
  const subjectName = subject?.client_name || 'Brazos Freight Co.';
  const subjectNo = subject ? `HFS-${(subject.id || '').slice(0, 12).toUpperCase()}` : 'HFS-TX-2024-10233';
  const subjectAmt = subject?.result?.final_claim_amount;
  const subjectExposure = typeof subjectAmt === 'number' && subjectAmt > 0 ? fmtUSD(subjectAmt) : null;

  const legend: Legend[] = [
    { label: 'Rule-based · 95%+', dotColor: P.greenSolid },
    { label: 'ML classifier · 85–95%', dotColor: P.textLabel },
    { label: 'Dual-AI · needs review', dotColor: P.orange },
    { label: 'Accepted', dotColor: P.textGhost },
  ];

  const navGroups: NavGroup[] = [
    { group: 'Work Desk', items: [{ id: 'desk', label: 'Work Desk', icon: '◧', active: true }] },
    { group: 'Cases', items: [{ id: 'cases', label: 'All Claims', icon: '▤' }, { id: 'new', label: 'New Claim', icon: '＋' }] },
    { group: 'Data', items: [
      { id: 'queue', label: 'Work Queue', icon: '▤', badge: String(queueCount) },
      { id: 'txn', label: 'Transactions', icon: '⇄', badge: '5' },
      { id: 'ingest', label: 'Ingestion', icon: '⤓' },
      { id: 'batch', label: 'Batch OCR', icon: '⊞' },
    ] },
    { group: 'Review', items: [{ id: 'anom', label: 'Anomalies', icon: '◬', badge: '7' }] },
    { group: 'Admin', items: [{ id: 'activity', label: 'Activity Log', icon: '≣' }] },
  ];

  // metric card chrome
  const metricCard = (accent: string): React.CSSProperties => ({
    background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderLeft: `3px solid ${accent}`,
    borderRadius: 13, padding: '18px 20px',
  });
  const metricVal: React.CSSProperties = {
    fontFamily: MONO, fontSize: 26, color: P.textPrimary, fontWeight: 500, fontVariantNumeric: 'tabular-nums',
  };

  const tierChipKind = (t: Tier): ChipKind => (t === 'rule' ? 'green' : t === 'ml' ? 'neutral' : 'orange');
  const ingestChipKind = (k: IngestKind): ChipKind => (k === 'done' ? 'green' : k === 'parsing' ? 'orange' : 'ghost');

  const txnGrid = '0.7fr 1.7fr 0.9fr 1.2fr 1.1fr';

  return (
    <div style={{ zoom: PREVIEW_ZOOM, height: '100vh', width: '100%', overflow: 'hidden', background: P.pageBg, fontFamily: BODY, color: P.textBody, display: 'flex', flexDirection: 'column' }}>
      {/* Static preview banner */}
      <div style={{ background: P.orange, color: '#1a1a1a', padding: '10px 20px', fontSize: 13.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 auto' }}>
        <span style={{ textTransform: 'uppercase', letterSpacing: '1px', border: '1.5px solid #1a1a1a', borderRadius: 4, padding: '2px 8px', fontSize: 10.5 }}>Static design preview</span>
        <span>These numbers are NOT live.</span>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* ============ LEFT RAIL ============ */}
      <aside style={{ width: 240, flex: '0 0 240px', background: P.pageBg, display: 'flex', flexDirection: 'column', height: '100%', borderRight: `1px solid ${P.railHairline}` }}>
        <div style={{ padding: '20px 20px 16px', display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: P.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 30px' }}>
            <div style={{ width: 12, height: 12, border: '2.5px solid #fefefe', borderRadius: '50%' }} />
          </div>
          <div style={{ lineHeight: 1 }}>
            <div style={{ fontFamily: SERIF, color: P.textPrimary, fontSize: 20, letterSpacing: '0.2px' }}>The Figure Co.</div>
            <div style={{ fontSize: 9.5, letterSpacing: '1.4px', color: P.textMuted, textTransform: 'uppercase', marginTop: 2 }}>Forensic BI Platform</div>
          </div>
        </div>
        <nav style={{ flex: 1, overflowY: 'auto', padding: '6px 0 16px' }}>
          {navGroups.map((grp) => (
            <div key={grp.group} style={{ margin: '10px 0 2px' }}>
              <div style={{ fontSize: 10, letterSpacing: '1.3px', color: P.textMuted, textTransform: 'uppercase', fontWeight: 600, padding: '6px 22px 4px' }}>{grp.group}</div>
              {grp.items.map((item) => (
                <NavRow key={item.id} P={P} item={item} />
              ))}
            </div>
          ))}
        </nav>
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: P.elevBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.textBody, fontWeight: 600, fontSize: 12.5, flex: '0 0 32px' }}>SO</div>
          <div style={{ flex: 1, lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ color: P.textPrimary, fontSize: 13, fontWeight: 500 }}>Sam Ortiz</div>
            <div style={{ color: P.textLabel, fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: P.orange, display: 'inline-block' }} />Data Specialist
            </div>
          </div>
          <span style={{ color: P.textMuted, fontSize: 15 }}>⌄</span>
        </div>
      </aside>

      {/* ============ MAIN ============ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>

        {/* TOPBAR */}
        <header style={{ height: 58, flex: '0 0 58px', background: P.pageBg, borderBottom: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', padding: '0 22px', gap: 14 }}>
          <div style={{ fontSize: 13, color: P.textPrimary, fontWeight: 500 }}>Work Desk</div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 9, background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 9, padding: '7px 12px', width: 280, cursor: 'text', color: P.textMuted, fontSize: 13 }}>
            <span style={{ fontSize: 13 }}>⌕</span><span style={{ flex: 1 }}>Search…</span>
            <span style={{ fontFamily: MONO, fontSize: 11, background: P.kbdBg, border: `1px solid ${P.controlBorder}`, borderRadius: 5, padding: '1px 6px', color: P.textLabel }}>⌘K</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 999, padding: '5px 12px', fontSize: 12, color: P.textBody, fontWeight: 500, whiteSpace: 'nowrap' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: P.orange, display: 'inline-block' }} />Data Specialist lens
          </div>
          <button style={{ width: 36, height: 36, borderRadius: 9, border: `1px solid ${P.cardBorder}`, background: P.elevBg, color: P.textBody, fontSize: 15, cursor: 'pointer' }}>◷</button>
          <ThemeToggle P={P} theme={theme} onToggle={toggle} />
        </header>

        {/* CONTENT */}
        <main style={{ flex: 1, overflow: 'auto', background: P.pageBg }}>
          <div style={{ padding: '28px 32px 48px', maxWidth: 1360, margin: '0 auto' }}>

            {/* HEADER */}
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22, gap: 20 }}>
              <div>
                <div style={{ fontSize: 12, letterSpacing: '1.4px', color: P.orangeText, textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>Data Specialist · Work Desk</div>
                <h1 style={{ fontFamily: SERIF, fontSize: 32, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Good morning, Sam</h1>
                <p style={{ margin: '6px 0 0', color: P.textLabel, fontSize: 14.5 }}>
                  <strong style={{ color: P.orangeText }}>{queueCount} transactions</strong> need a decision and{' '}
                  <strong style={{ color: P.textPrimary }}>{wizardClaims.length || 2} wizards</strong> are mid-intake.
                </p>
              </div>
              <button style={{ background: P.orange, color: P.onOrange, border: 'none', borderRadius: 10, padding: '11px 20px', fontSize: 14, fontWeight: 600, fontFamily: BODY, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>＋ New Claim · Wizard v2</button>
            </div>

            {/* METRIC RAIL */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
              <div style={metricCard(P.orange)}>
                <div style={{ fontSize: 12.5, color: P.textLabel, fontWeight: 500, marginBottom: 10 }}>Transactions to classify</div>
                <div style={{ ...metricVal, color: P.orangeText }}>{queueCount}</div>
                <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 7 }}>5 below confidence threshold</div>
              </div>
              <div style={metricCard(P.accentNeutral)}>
                <div style={{ fontSize: 12.5, color: P.textLabel, fontWeight: 500, marginBottom: 10 }}>Claims in data stage</div>
                <div style={metricVal}>{dataStageCount || 4}</div>
                <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 7 }}>2 ready to advance</div>
              </div>
              <div style={metricCard(P.accentNeutral)}>
                <div style={{ fontSize: 12.5, color: P.textLabel, fontWeight: 500, marginBottom: 10 }}>Docs awaiting OCR</div>
                <div style={metricVal}>9</div>
                <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 7 }}>1 batch queued</div>
              </div>
              <div style={metricCard(P.accentNeutral)}>
                <div style={{ fontSize: 12.5, color: P.textLabel, fontWeight: 500, marginBottom: 10 }}>Auto-classify rate</div>
                <div style={{ ...metricVal, color: P.greenText }}>92%</div>
                <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 7 }}>rule + ML, last 30d</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>

              {/* TRANSACTION REVIEW QUEUE */}
              <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${P.divider}`, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <h2 style={{ fontFamily: SERIF, fontSize: 18, color: P.textPrimary, margin: 0, fontWeight: 400, flex: 1 }}>Transaction Review Queue</h2>
                  <span style={{ fontSize: 12, color: P.textLabel }}>
                    {subjectName} · <span style={{ fontFamily: MONO, color: P.textBody }}>{subjectNo}</span>
                    {subjectExposure ? <span style={{ fontFamily: MONO, color: P.textBody }}> · {subjectExposure}</span> : null}
                    <span style={{ color: P.textMuted }}> · updated {relTime(subject?.updated_at || subject?.created_at)}</span>
                  </span>
                </div>
                {/* classifier legend */}
                <div style={{ padding: '11px 20px', borderBottom: `1px solid ${P.divider}`, display: 'flex', gap: 18, alignItems: 'center' }}>
                  {legend.map((g) => (
                    <div key={g.label} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: g.dotColor, display: 'inline-block' }} />
                      <span style={{ fontSize: 11.5, color: P.textLabel }}>{g.label}</span>
                    </div>
                  ))}
                </div>
                {/* column heads */}
                <div style={{ display: 'grid', gridTemplateColumns: txnGrid, padding: '9px 20px', borderBottom: `1px solid ${P.divider}`, fontSize: 10.5, letterSpacing: '0.6px', textTransform: 'uppercase', color: P.textMuted, fontWeight: 600 }}>
                  <span>Date</span><span>Description</span><span style={{ textAlign: 'right' }}>Amount</span><span>Suggested category</span><span>Confidence</span>
                </div>
                {TXNS.map((t, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: txnGrid, padding: '13px 20px', borderBottom: `1px solid ${P.rowDivider}`, alignItems: 'center', background: t.review ? P.orangeRowTint : 'transparent' }}>
                    <span style={{ fontFamily: MONO, fontSize: 12, color: P.textLabel }}>{t.date}</span>
                    <span style={{ fontSize: 13, color: P.textPrimary, fontWeight: 500 }}>{t.desc}</span>
                    <span style={{ fontFamily: MONO, fontSize: 13, color: P.textBody, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{t.amount}</span>
                    <span style={{ fontSize: 12.5, color: t.review ? P.orangeText : P.textBody }}>{t.cat}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ ...chipStyle(tierChipKind(t.tier), P), fontSize: 10.5, padding: '2px 8px' }}>{t.tierLabel}</span>
                      <span style={{ fontFamily: MONO, fontSize: 11.5, color: P.textMuted }}>{t.pct}</span>
                    </span>
                  </div>
                ))}
                <div style={{ padding: '13px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button style={{ background: P.orange, color: P.onOrange, border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: BODY }}>Accept all auto-classified (18)</button>
                  <span style={{ fontSize: 12, color: P.textMuted }}>5 need a manual decision</span>
                </div>
              </div>

              {/* RIGHT: WIZARDS + INGESTION */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: `1px solid ${P.divider}` }}>
                    <h3 style={{ fontFamily: SERIF, fontSize: 16, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Active Wizards</h3>
                  </div>
                  {wizardRows.map((w) => (
                    <div
                      key={w.key}
                      onClick={w.id ? () => navigate(`/report/${w.id}`) : undefined}
                      style={{ padding: '14px 18px', borderBottom: `1px solid ${P.rowDivider}`, cursor: 'pointer' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 13.5, color: P.textPrimary, fontWeight: 500 }}>{w.name}</span>
                        <span style={{ fontSize: 11.5, color: P.textLabel }}>Step {w.step}/7</span>
                      </div>
                      <div style={{ height: 4, background: P.divider, borderRadius: 999, overflow: 'hidden', marginBottom: 6 }}>
                        <div style={{ height: '100%', borderRadius: 999, background: P.orange, width: w.pct }} />
                      </div>
                      <div style={{ fontSize: 11.5, color: P.textMuted }}>{w.stage}</div>
                    </div>
                  ))}
                  <div style={{ padding: '12px 18px', fontSize: 12.5, color: P.orangeText, fontWeight: 600, cursor: 'pointer' }}>＋ Start new claim →</div>
                </div>

                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: `1px solid ${P.divider}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h3 style={{ fontFamily: SERIF, fontSize: 16, color: P.textPrimary, margin: 0, fontWeight: 400, flex: 1 }}>Ingestion</h3>
                    <span style={{ fontSize: 11, color: P.textMuted }}>5-tier extraction</span>
                  </div>
                  {INGEST.map((it) => (
                    <div key={it.name} style={{ padding: '12px 18px', borderBottom: `1px solid ${P.rowDivider}`, display: 'flex', gap: 11, alignItems: 'center' }}>
                      <span style={{ width: 30, height: 30, borderRadius: 7, background: P.elevBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: P.textLabel, flex: '0 0 30px' }}>{it.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: P.textPrimary, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name}</div>
                        <div style={{ fontSize: 11, color: P.textMuted, marginTop: 1 }}>{it.meta}</div>
                      </div>
                      <span style={{ ...chipStyle(ingestChipKind(it.kind), P), fontSize: 11, padding: '3px 9px' }}>{it.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      </div>
    </div>
  );
}
